import { logger } from '@adornis/base/logging.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import { subscribePaginatedAggregation } from '@adornis/baseql/server/resolver/mongo.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import type { IntAspect } from '@adornis/filter/aspect-schemas/IntAspect.js';
import type { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { DateTime } from 'luxon';
import { catchError, from, mapTo, of, switchMap } from 'rxjs';
import { ChancenDynamicClassProduct } from '../../../_cms/products/entities/ChancenDynamicClassProduct.js';
import { KpfFilter } from '../../../filter/KpfFilter.js';
import { SemanticRelationship } from '../../../semantics/SemanticRelationship.js';
import type { BundeslandAspect } from '../../aspects/BundeslandAspect.js';
import type { ZipCityAspect } from '../../aspects/ZipCityAspect.js';
import { School } from '../../partner/entities/School.js';
import type { ChancenwerkUserAspect } from '../../users/entities/aspects/ChancenwerkUserAspect.js';
import { KPF } from '../entities/KPF.js';
import { subscribeKPFByAttendance } from './subscribeKPFByAttendance.js';

export const escapeRegex = (value: string) => value.replace(/([^a-zA-Z0-9\s])/g, '\\$1');

const resolveSubscribeKPFsBySchoolIDs = (schoolIDs: string[]) => (gqlFields: BaseQLSelectionSet<KPF>) => {
  const selector = { schoolID: { $in: schoolIDs } };
  const handle = getCollectionHandle<KPF>(KPF._collectionName);
  return from(publishFilter(KPF._class)).pipe(
    switchMap(publishFilter => {
      return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector, {
        sort: { kuerzel: -1 },
      });
    }),
    catchError(err => {
      logger.error({ err }, 'Error during subscription');
      return of([] as Array<KPF>);
    }),
  );
};

export const subscribeKPFsBySchoolIDs = registerSubscription({
  type: () => [KPF],
  operationName: 'subscribeKPFsBySchoolIDs',
  params: [{ type: () => [String], name: 'schoolIDs' }],
  resolve: resolveSubscribeKPFsBySchoolIDs,
});

// TODO what the fuck is this? Remove it
function historyAggregation() {
  const aggregation = [
    {
      $lookup: {
        from: 'eventlog',
        localField: '_id', // Field from the "KPF" collection
        foreignField: 'kpfID', // Field from the "AttendanceExportEventlog" collection
        as: 'attendanceLogs',
      },
    },
    { $addFields: { attendanceLogsCount: { $size: '$attendanceLogs' } } },
    { $match: { attendanceLogsCount: { $gt: 0 } } },
    { $sort: { attendanceLogsCount: -1 } },
  ];

  return aggregation;
}

function searchAggregation(filter: KpfFilter, historyMode?: boolean) {
  const kuerzelAspect = filter.getFilterAspect<StringAspect>('kuerzel');
  const employeeAspect = filter.getFilterAspect<ChancenwerkUserAspect>('employeeOnKPF');
  const booleanAspect = filter.getFilterAspect<BooleanAspect>('active');
  const stateAspect = filter.getFilterAspect<BundeslandAspect>('stateOfPartner');
  const zipCityAspect = filter.getFilterAspect<ZipCityAspect>('zipCityOfPartner');
  const partnerNameAspect = filter.getFilterAspect<StringAspect>('partnerName');
  const partnerClassAspect = filter.getFilterAspect<StringAspect>('partnerClass');
  const partnerFunding = filter.getFilterAspect<StringAspect>('partnerFunding');
  const acquisitionCount = filter.getFilterAspect<IntAspect>('acquisitionCount');

  let acquisitionMatch: any = [];
  if (acquisitionCount && (acquisitionCount.min || acquisitionCount.max)) {
    acquisitionMatch = [
      {
        $lookup: {
          from: 'acquisition-record',
          localField: '_id',
          foreignField: 'kpfID',
          as: 'acquisitionRecords',
          pipeline: [
            { $project: { kpfID: 1, createdStudents: 1, numberOfCreatedStudents: { $size: '$createdStudents' } } },
          ],
        },
      },
      { $addFields: { acquisitionRecordsSize: { $size: '$acquisitionRecords' } } },
    ];

    if (acquisitionCount.min && acquisitionCount.min !== 0) {
      acquisitionMatch.push({
        $match: { 'acquisitionRecords.numberOfCreatedStudents': { $gte: acquisitionCount.min } },
      });
    }
    if (acquisitionCount.max) {
      acquisitionMatch.push({
        $match: {
          $or: [
            { 'acquisitionRecords.numberOfCreatedStudents': { $lte: acquisitionCount.max } },
            { acquisitionRecordsSize: { $eq: 0 } },
          ],
        },
      });
    }
  }

  // Aggregate (join) neccessary relationship tables, then filter by kpf filter
  const aggregation = [
    ...(booleanAspect?.value === true
      ? [
          {
            $match: { $or: [{ endDatum: { $gte: new Date() } }, { endDatum: { $exists: false } }, { endDatum: null }] },
          },
        ]
      : booleanAspect?.value === false
        ? [{ $match: { endDatum: { $lt: new Date(), $exists: true } } }]
        : []),

    { $lookup: { from: School._collectionName, as: 'schools', localField: 'schoolID', foreignField: '_id' } },
    { $addFields: { school: { $arrayElemAt: ['$schools', 0] } } },
    {
      $lookup: {
        from: ChancenDynamicClassProduct._collectionName,
        as: 'dynamicProduct',
        localField: 'produkt._class',
        foreignField: 'class',
      },
    },
    {
      $lookup: {
        from: SemanticRelationship._collectionName,
        let: { kpfId: '$_id' },
        pipeline: [
          {
            $match: {
              $expr: { $eq: ['$objectID', '$$kpfId'] },
              $or: [
                { endDate: null },
                { endDate: undefined },

                // * endDate in the DB is the start of the day, but we want to keep active taetigkeiten until the end of the day
                // * so we need to subtract one day from the current time and set it to the end of the day.
                { endDate: { $gt: DateTime.now().minus({ day: 1 }).endOf('day').toJSDate() } },
              ],
            },
          },
        ],
        as: 'relations',
      },
    },

    { $addFields: { product_single: { $arrayElemAt: ['$dynamicProduct', 0] } } },
    // kuerzel for sorting and filtering
    { $addFields: { kuerzel: { $concat: ['$school.kuerzel', '_', '$product_single.kuerzel'] } } },

    {
      $match: {
        $and: [
          ...(kuerzelAspect?.value || filter.search || partnerNameAspect?.value
            ? [
                {
                  $or: [
                    ...(kuerzelAspect?.value
                      ? [
                          {
                            kuerzel: {
                              $regex: kuerzelAspect.value.replaceAll(/([^\w])/g, '\\$1'),
                              $options: 'i',
                            },
                          },
                        ]
                      : []),
                    ...(filter.search
                      ? [{ kuerzel: { $regex: filter.search.replaceAll(/([^\w])/g, '\\$1'), $options: 'i' } }]
                      : []),
                    ...(partnerNameAspect?.value || filter.search
                      ? [
                          {
                            'school.name': {
                              $regex:
                                filter.search?.replaceAll(/([^\w])/g, '\\$1') ??
                                partnerNameAspect?.value?.split(',').map(escapeRegex).join('|'),
                              $options: 'i',
                            },
                          },
                        ]
                      : []),
                  ],
                },
              ]
            : []),

          partnerClassAspect?.value ? { 'school._class': partnerClassAspect.value } : {},
          zipCityAspect?.zipCity?.city
            ? {
                'school.address.zipCity.city': {
                  $regex: zipCityAspect.zipCity.city.replaceAll(/([^\w])/g, '\\$1'),
                  $options: 'i',
                },
              }
            : {},
          zipCityAspect?.zipCity?.zip
            ? {
                'school.address.zipCity.zip': {
                  $regex: zipCityAspect.zipCity.zip.replaceAll(/([^\w])/g, '\\$1'),
                  $options: 'i',
                },
              }
            : {},
          stateAspect?.value
            ? { 'school.bundesland': { $regex: stateAspect.value.replaceAll(/([^\w])/g, '\\$1'), $options: 'i' } }
            : {},
          employeeAspect?.value ? { 'relations.subjectID': employeeAspect.value } : {},
          (filter.zipCities?.length ?? 0) > 0 ? filter.getZipCitiesFilter() : {},
          partnerFunding?.value ? { 'ragPartnerInformation.ragPartnerIsID': partnerFunding?.value } : {},
        ],
      },
    },

    ...acquisitionMatch,

    { $sort: filter.getMongoOptions().sort },
  ];

  if (historyMode) {
    return [...aggregation, ...historyAggregation()];
  }

  return aggregation;
}

const resolveSubscribeKPFBySearch =
  (skip: number, limit: number, filter: KpfFilter) => (gqlFields: BaseQLSelectionSet<KPF>) => {
    return subscribePaginatedAggregation(KPF, skip, limit, filter.getMongoFilter(), searchAggregation(filter, false));
  };

const resolveSubscribeHistoryKPFBySearch =
  (skip: number, limit: number, filter: KpfFilter) => (gqlFields: BaseQLSelectionSet<KPF>) => {
    return subscribePaginatedAggregation(KPF, skip, limit, filter.getMongoFilter(), searchAggregation(filter, true));
  };

export const subscribeKPFBySearch = registerSubscription({
  type: () => [KPF],
  operationName: 'subscribeKPFBySearch',
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => KpfFilter, name: 'filter' },
  ],
  resolve: resolveSubscribeKPFBySearch,
});

export const subscribeHistoryKPFBySearch = registerSubscription({
  type: () => [KPF],
  operationName: 'subscribeHistoryKPFBySearch',
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => KpfFilter, name: 'filter' },
  ],
  resolve: resolveSubscribeHistoryKPFBySearch,
});

const resolveSubscribeNumberOfKPFsBySearch = (filter: KpfFilter) => () => {
  const handle = getCollectionHandle<KPF>(KPF._collectionName);
  return from(publishFilter(KPF._class)).pipe(
    switchMap(publishFilter => {
      const selector = filter.getMongoFilter();

      return handle
        .watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector)
        .pipe(mapTo({ ...selector, ...publishFilter }));
    }),
    switchMap(async docs => {
      const collection = await getRawCollection<KPF>(KPF._collectionName);
      const result = await collection
        .aggregate([{ $match: docs }, ...searchAggregation(filter), { $count: 'count' }])
        .toArray();

      return (result[0]?.count as number) ?? 0;
    }),
  );
};

export const subscribeNumberOfKPFsBySearch = registerSubscription({
  type: () => Int,
  operationName: 'subscribeNumberOfKPFsBySearch',
  params: [{ type: () => KpfFilter, name: 'filter' }],
  resolve: resolveSubscribeNumberOfKPFsBySearch,
});

export namespace KPFAPI {
  export const subscribeCountByFilter = subscribeNumberOfKPFsBySearch;
  export const subscribeByFilter = subscribeKPFBySearch;
  export const subscribeHistoryByFilter = subscribeHistoryKPFBySearch;
  export const subscribeByAttendance = subscribeKPFByAttendance;
}
